import React, { useState, useEffect } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Users, Search, AlertCircle, Shield } from 'lucide-react';
import './dashboard.css';
import UserProfile from './UserProfile';

const UserDashboard = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const [employees, setEmployees] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setHasUserAccess] = useState(false);

  useEffect(() => {
    const initializeUserData = async () => {
      if (authState?.isAuthenticated) {
        try {
          const user = await oktaAuth.getUser();
          const groups = user.groups || [];
          setUserGroups(groups);
          
          const hasAccess = groups.some(group => 
            group.toLowerCase().includes('user')
          );
          setHasUserAccess(hasAccess);
          
          if (!hasAccess) {
            setError('Access denied. You need to be in a group containing "User" to access this dashboard.');
            setLoading(false);
            return;
          }

          const userEmail = authState.idToken?.claims?.email;
          const userName = authState.idToken?.claims?.name;
          
          if (!userEmail && !userName) {
            setError('Unable to identify user. Please contact your administrator.');
            setLoading(false);
            return;
          }
          const response = await fetch('/api/employees?limit=1000&offset=0');
          if (!response.ok) {
            throw new Error('Failed to fetch employee data');
          }
          
          const allEmployees = await response.json();
          const foundUser = allEmployees.find(emp => 
            emp.name.toLowerCase() === userName?.toLowerCase() ||
            emp.name.toLowerCase().includes(userName?.toLowerCase().split(' ')[0]) ||
            emp.name.toLowerCase().includes(userName?.toLowerCase().split(' ')[1])
          );

          if (!foundUser) {
            setError('Your employee record was not found. Please contact your administrator.');
            setLoading(false);
            return;
          }

          setCurrentUser(foundUser);
          const teamMembers = allEmployees.filter(emp => 
            emp.manager === foundUser.manager && 
            emp.department === foundUser.department
          );

          setEmployees(teamMembers);
          setLoading(false);

        } catch (error) {
          console.error('Error initializing user data:', error);
          setError('Failed to load user data. Please try again.');
          setLoading(false);
        }
      }
    };

    initializeUserData();
  }, [authState, oktaAuth]);

  const filteredEmployees = employees.filter(emp => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      emp.name.toLowerCase().includes(query) ||
      emp.role.toLowerCase().includes(query) ||
      emp.department.toLowerCase().includes(query) ||
      emp.manager.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="main-content">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div className="loading-spinner"></div>
            <div>Loading your team information...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="main-content">
          <UserProfile />
          <div style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '8px',
            padding: '20px',
            margin: '20px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={24} color="#d63031" />
            <div>
              <h3 style={{ margin: '0 0 8px 0', color: '#d63031' }}>Access Restricted</h3>
              <p style={{ margin: 0, color: '#666' }}>{error}</p>
              {userGroups.length > 0 && (
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
                  Your current groups: {userGroups.join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="logo">
          <div className="logo-icon">
            <img src="/ryder.png" alt="Ryder" className="logo-image" />
          </div>
          <span className="logo-text">Ryder</span>
        </div>
        <div>
          <div className="menu-header">USER DASHBOARD</div>
          <div className="menu-item active">
            <Users className="menu-icon" />
            <span>My Team</span>
          </div>
          <div className="menu-item" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
            <Shield className="menu-icon" />
            <span>Team Access Only</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <UserProfile />

        {/* Header */}
        <div className="header">
          <div className="header-text">
            <h1>My Team Dashboard</h1>
            <p>View your team members in {currentUser?.department} under {currentUser?.manager}</p>
          </div>
          <div className="header-actions">
            <div className="access-badge">
              <Shield size={16} />
              <span>User Access</span>
            </div>
          </div>
        </div>

        {/* Team Info Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#6c5ce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px'
            }}>
              <Users size={24} />
            </div>
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', color: '#333' }}>
                {currentUser?.department} Team
              </h2>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                Manager: {currentUser?.manager} • {filteredEmployees.length} team members
              </p>
            </div>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginTop: '16px'
          }}>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c5ce7' }}>
                {employees.length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>Total Team Members</div>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#00b894' }}>
                {userGroups.filter(g => g.toLowerCase().includes('user')).length}
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>User Groups</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="search-filters" style={{ marginBottom: '24px' }}>
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search team members by name, role..."
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Team Members */}
        <div className="manager-section">
          <div className="manager-header">
            <div className="manager-info">
              <div className="manager-icon">
                <Users className="menu-icon" />
              </div>
              <span className="manager-title">{currentUser?.manager}</span>
            </div>
            <span className="department-count">
              {filteredEmployees.length} team member{filteredEmployees.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="department-section">
            <div className="department-header">
              <div className="department-info">
                <div className="department-icon">
                  <div className="department-icon-inner"></div>
                </div>
                <span className="department-title">{currentUser?.department}</span>
              </div>
              <span className="member-count">{filteredEmployees.length} members</span>
            </div>

            <div className="member-grid">
              {filteredEmployees.map(member => (
                <div key={member.id} className="member-card">
                  <div className="member-header">
                    <div className="member-info">
                      <div className="avatar">
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="member-details">
                        <h3>{member.name}</h3>
                        <p>{member.name.toLowerCase().replace(' ', '.') + '@company.com'}</p>
                        <p>{member.role}</p>
                      </div>
                    </div>
                    <span className="status-badge status-active">
                      {member.id === currentUser?.id ? 'You' : 'Active'}
                    </span>
                  </div>
                  <div className="member-metadata">
                    <div className="metadata-row" style={{ flex: 1 }}>
                      <span className="metadata-label">Department:</span>
                      <span>{member.department}</span>
                    </div>
                    <div className="metadata-row" style={{ flex: 1 }}>
                      <span className="metadata-label">Manager:</span>
                      <span>{member.manager}</span>
                    </div>
                    <div className="metadata-row" style={{ flex: 1 }}>
                      <span className="metadata-label">Employee ID:</span>
                      <span>{member.id}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredEmployees.length === 0 && searchQuery && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#666'
              }}>
                <Search size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <p>No team members found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
